import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { requestPasswordReset } from '../components/authentication/functions/api_calls';
import { readAxiosDetail } from '../components/company/api_calls';

export default function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    if (!email.trim()) {
      setError(t('forgotPassword.missingEmail'));
      return;
    }

    setSubmitting(true);
    try {
      await requestPasswordReset(email.trim());
      setSuccess(true);
    } catch (e) {
      setError(readAxiosDetail(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CenteredContainer>
      <Paper sx={{ p: 4, maxWidth: 440, width: '100%' }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {t('forgotPassword.title')}
        </Typography>

        {success ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              {t('forgotPassword.successMessage')}
            </Alert>
            <Button fullWidth variant="outlined" component={RouterLink} to="/">
              {t('forgotPassword.backToLogin')}
            </Button>
          </>
        ) : (
          <>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              {t('forgotPassword.instructions')}
            </Typography>

            {error && (
              <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label={t('forgotPassword.emailLabel')}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              size="small"
              required
              sx={{ mb: 3 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void handleSubmit();
              }}
            />

            <Button
              fullWidth
              variant="contained"
              onClick={() => void handleSubmit()}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={20} /> : t('forgotPassword.submitButton')}
            </Button>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              <RouterLink to="/" style={{ color: 'inherit' }}>{t('forgotPassword.backToLogin')}</RouterLink>
            </Typography>
          </>
        )}
      </Paper>
    </CenteredContainer>
  );
}

function CenteredContainer({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      {children}
    </Box>
  );
}
